import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";

import type { UserFeedbackRecord } from "../ChatRecord";

/**
 * Fetch feedback the current user has submitted on ``chatId``.
 *
 * ``chatOrgId`` is forwarded as ``X-Roboto-Org-Id`` so the backend's
 * ``REQUIRES_CALLER_ORG`` auth rule has an org to pin on, which is required
 * for callers who belong to more than one org.
 */
export function useChatFeedback(
  chatId: string | undefined,
  chatOrgId?: string,
): ReturnType<typeof useQuery<UserFeedbackRecord[]>> {
  const { chatService } = useDomainServices();

  return useQuery<UserFeedbackRecord[]>({
    // ``chatOrgId`` is part of the request scope (sent as ``X-Roboto-Org-Id``)
    // so it must be in the cache key — two orgs viewing the same chat id
    // (admin browsing, or a multi-org user switching orgs) would otherwise
    // collide on a single cache entry and serve each other's responses.
    queryKey: ["chat-feedback", chatId ?? "", chatOrgId ?? ""],
    queryFn: ({ signal }) =>
      chatService.getFeedbackForChat(chatId as string, {
        abortSignal: signal,
        orgId: chatOrgId,
      }),
    enabled: !!chatId,
    staleTime: 30_000,
  });
}

/**
 * Index of feedback records by ``message_sequence_num`` for O(1) per-message
 * lookups. The chat view renders one ``ChatFeedback`` per assistant message
 * and previously did a linear scan inside each — N messages × N find() = O(N²)
 * per render. Building the map once per ``existingFeedback`` change collapses
 * that to O(N) total.
 */
export function useChatFeedbackBySequenceNum(
  chatId: string | undefined,
  chatOrgId?: string,
): Map<number, UserFeedbackRecord> {
  const { data } = useChatFeedback(chatId, chatOrgId);
  return React.useMemo(() => {
    const map = new Map<number, UserFeedbackRecord>();
    for (const record of data ?? []) {
      map.set(record.message_sequence_num, record);
    }
    return map;
  }, [data]);
}
